import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
} from "recharts";
import type { BacktestStats, EquityPoint } from "@/lib/backtest-stats";
import { formatMoney, formatPct } from "@/lib/backtest-stats";
import { useFormatDate } from "@/hooks/useFormatDate";

const SHARPE_TIP = [
  "Interpretation:",
  "> 1.0 → good",
  "> 2.0 → very good",
  "> 3.0 → exceptional",
  "< 1.0 → weak risk-adjusted performance",
];

const SORTINO_TIP = [
  "Interpretation:",
  "> 1.0 → good",
  "> 2.0 → very good",
  "> 3.0 → excellent, especially for trend-following systems",
];

export function StatsGrid({ stats }: { stats: BacktestStats }) {
  const closedTrades = stats.winning_trades + stats.losing_trades;
  const items: { label: string; value: ReactNode; tone?: "profit" | "loss"; tip?: string[] }[] = [
    { label: "Net Profit", value: `${formatMoney(stats.net_profit)} (${formatPct(stats.net_profit_pct)})`, tone: stats.net_profit >= 0 ? "profit" : "loss" },
    { label: "Percent Profitable", value: `${(stats.win_rate * 100).toFixed(1)}%` },
    { label: "Won Trades", value: `${stats.winning_trades} / ${closedTrades}` },
    { label: "Profit Factor", value: stats.profit_factor.toFixed(2) },
    { label: "Max Drawdown", value: `${formatMoney(-Math.abs(stats.max_drawdown))} (${formatPct(-Math.abs(stats.max_drawdown_pct))})`, tone: "loss" },
    {
      label: "Avg Win / Loss",
      value: (
        <>
          <span style={{ color: "var(--trading-profit)" }}>{formatMoney(stats.avg_win_trade)}</span>
          <span className="text-[var(--text-muted)]"> / </span>
          <span style={{ color: "var(--trading-loss)" }}>{formatMoney(Math.abs(stats.avg_loss_trade) * -1)}</span>
        </>
      ),
    },
    { label: "Sharpe", value: stats.sharpe_ratio.toFixed(2), tip: SHARPE_TIP },
    { label: "Sortino", value: stats.sortino_ratio.toFixed(2), tip: SORTINO_TIP },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      {items.map((it) => (
        <div key={it.label} className="rounded-card border border-[var(--border)] bg-[var(--bg-card-elevated)] p-3">
          <div className="flex items-center gap-1 text-xs text-[var(--text-secondary)]">
            <span>{it.label}</span>
            {it.tip && (
              <span tabIndex={0} className="group relative inline-flex cursor-help rounded-full outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]">
                <span
                  className="rounded-full border border-[var(--border)] px-1 text-[10px] leading-none text-[var(--text-muted)]"
                  aria-label={`${it.label} interpretation`}
                >
                  ?
                </span>
                <span
                  role="tooltip"
                  className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-1.5 hidden w-56 -translate-x-1/2 rounded-md border border-[var(--border)] bg-[var(--bg-card)] px-2.5 py-2 text-left text-[11px] leading-relaxed text-[var(--text-secondary)] shadow-lg group-hover:block group-focus-within:block"
                >
                  {it.tip.map((line) => (
                    <div key={line} className={line === "Interpretation:" ? "mb-1 font-medium text-[var(--text-primary)]" : undefined}>
                      {line}
                    </div>
                  ))}
                </span>
              </span>
            )}
          </div>
          <div
            className="mt-1 text-lg font-semibold"
            style={{
              color:
                it.tone === "profit"
                  ? "var(--trading-profit)"
                  : it.tone === "loss"
                    ? "var(--trading-loss)"
                    : "var(--text-primary)",
            }}
          >
            {it.value}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Hero + secondary KPI hierarchy for the lab workspace Overview tab. */
export function HeroStatsOverview({ stats }: { stats: BacktestStats }) {
  const closedTrades = stats.winning_trades + stats.losing_trades;
  const profit = stats.net_profit >= 0;
  const secondary: { label: string; value: ReactNode; tip?: string[] }[] = [
    { label: "Win Rate", value: `${(stats.win_rate * 100).toFixed(1)}%` },
    { label: "Profit Factor", value: stats.profit_factor.toFixed(2) },
    { label: "Total Trades", value: `${closedTrades}` },
    { label: "Won / Lost", value: `${stats.winning_trades} / ${stats.losing_trades}` },
    { label: "Sharpe", value: stats.sharpe_ratio.toFixed(2), tip: SHARPE_TIP },
    { label: "Sortino", value: stats.sortino_ratio.toFixed(2), tip: SORTINO_TIP },
  ];

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <div
          className="rounded-card border p-4"
          style={{
            borderColor: profit
              ? "color-mix(in srgb, var(--trading-profit) 35%, var(--border))"
              : "color-mix(in srgb, var(--trading-loss) 35%, var(--border))",
            background: profit
              ? "color-mix(in srgb, var(--trading-profit) 10%, var(--bg-card-elevated))"
              : "color-mix(in srgb, var(--trading-loss) 10%, var(--bg-card-elevated))",
          }}
        >
          <div className="text-xs font-medium uppercase tracking-wide text-[var(--text-secondary)]">
            Net Profit
          </div>
          <div
            className="mt-1 text-3xl font-semibold tabular-nums tracking-tight"
            style={{ color: profit ? "var(--trading-profit)" : "var(--trading-loss)" }}
          >
            {formatMoney(stats.net_profit)}
          </div>
          <div
            className="mt-0.5 text-sm font-medium tabular-nums"
            style={{ color: profit ? "var(--trading-profit)" : "var(--trading-loss)" }}
          >
            {formatPct(stats.net_profit_pct)}
          </div>
        </div>
        <div className="rounded-card border border-[var(--border)] bg-[var(--bg-card-elevated)] p-4">
          <div className="text-xs font-medium uppercase tracking-wide text-[var(--text-secondary)]">
            Max Drawdown
          </div>
          <div className="mt-1 text-3xl font-semibold tabular-nums tracking-tight text-[var(--trading-loss)]">
            {formatMoney(-Math.abs(stats.max_drawdown))}
          </div>
          <div className="mt-0.5 text-sm font-medium tabular-nums text-[var(--trading-loss)]">
            {formatPct(-Math.abs(stats.max_drawdown_pct))}
          </div>
        </div>
        <div className="rounded-card border border-[var(--border)] bg-[var(--bg-card-elevated)] p-4">
          <div className="text-xs font-medium uppercase tracking-wide text-[var(--text-secondary)]">
            Avg Win / Loss
          </div>
          <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-3xl font-semibold tabular-nums tracking-tight">
            <span style={{ color: "var(--trading-profit)" }}>{formatMoney(stats.avg_win_trade)}</span>
            <span className="text-[var(--text-muted)]">/</span>
            <span style={{ color: "var(--trading-loss)" }}>
              {formatMoney(Math.abs(stats.avg_loss_trade) * -1)}
            </span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {secondary.map((it) => (
          <div
            key={it.label}
            className="rounded-card border border-[var(--border)] bg-[var(--bg-card-elevated)] px-3 py-2.5"
          >
            <div className="flex items-center gap-1 text-[11px] text-[var(--text-secondary)]">
              <span>{it.label}</span>
              {it.tip && (
                <span tabIndex={0} className="group relative inline-flex cursor-help rounded-full outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]">
                  <span
                    className="rounded-full border border-[var(--border)] px-1 text-[10px] leading-none text-[var(--text-muted)]"
                    aria-label={`${it.label} interpretation`}
                  >
                    ?
                  </span>
                  <span
                    role="tooltip"
                    className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-1.5 hidden w-56 -translate-x-1/2 rounded-md border border-[var(--border)] bg-[var(--bg-card)] px-2.5 py-2 text-left text-[11px] leading-relaxed text-[var(--text-secondary)] shadow-lg group-hover:block group-focus-within:block"
                  >
                    {it.tip.map((line) => (
                      <div
                        key={line}
                        className={
                          line === "Interpretation:"
                            ? "mb-1 font-medium text-[var(--text-primary)]"
                            : undefined
                        }
                      >
                        {line}
                      </div>
                    ))}
                  </span>
                </span>
              )}
            </div>
            <div className="mt-1 text-base font-semibold tabular-nums text-[var(--text-primary)]">
              {it.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function EquityCurve({
  points,
  startDate,
  endDate,
  className = "h-64 w-full",
}: {
  points: EquityPoint[];
  startDate?: string | null;
  endDate?: string | null;
  /** Height / sizing for the chart container (default h-64). */
  className?: string;
}) {
  const { chartDate, dateTime } = useFormatDate();
  const gradId = useId().replace(/:/g, "");
  const hostRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (!cr) return;
      const width = Math.max(0, Math.floor(cr.width));
      const height = Math.max(0, Math.floor(cr.height));
      setSize((prev) => (prev.width === width && prev.height === height ? prev : { width, height }));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!points.length) {
    return <div className="py-8 text-center text-sm text-[var(--text-muted)]">No equity curve data</div>;
  }

  const startMs = startDate ? new Date(startDate).getTime() : NaN;
  const endMs = endDate ? new Date(endDate).getTime() : NaN;
  const domainMin = Number.isFinite(startMs) ? startMs : points[0]!.t;
  const domainMax = Number.isFinite(endMs) ? endMs : points[points.length - 1]!.t;

  let pnlMin = 0;
  let pnlMax = 0;
  for (const p of points) {
    if (p.pnl < pnlMin) pnlMin = p.pnl;
    if (p.pnl > pnlMax) pnlMax = p.pnl;
  }
  // Vertical gradient stop at y=0 (SVG y grows downward: 0% = top = max).
  const span = pnlMax - pnlMin || 1;
  const zeroOffset = `${((pnlMax - 0) / span) * 100}%`;
  // Solid colors: CSS vars often fail as SVG <stop stopColor>.
  const profitColor = "#22C55E";
  const lossColor = "#EF4444";

  return (
    <div ref={hostRef} className={className}>
      {size.width > 0 && size.height > 0 ? (
        <LineChart
          width={size.width}
          height={size.height}
          data={points}
          margin={{ top: 8, right: 8, left: 0, bottom: 4 }}
        >
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              {pnlMax <= 0 ? (
                <stop offset="0%" stopColor={lossColor} />
              ) : pnlMin >= 0 ? (
                <stop offset="0%" stopColor={profitColor} />
              ) : (
                <>
                  <stop offset="0%" stopColor={profitColor} />
                  <stop offset={zeroOffset} stopColor={profitColor} />
                  <stop offset={zeroOffset} stopColor={lossColor} />
                  <stop offset="100%" stopColor={lossColor} />
                </>
              )}
            </linearGradient>
          </defs>
          <CartesianGrid stroke="var(--divider)" strokeDasharray="3 3" />
          <XAxis
            dataKey="t"
            type="number"
            domain={[domainMin, domainMax]}
            scale="time"
            stroke="var(--text-muted)"
            tick={{ fill: "var(--text-muted)", fontSize: 11 }}
            tickFormatter={(v) => chartDate(new Date(Number(v)).toISOString())}
            minTickGap={48}
          />
          <YAxis stroke="var(--text-muted)" tick={{ fill: "var(--text-muted)", fontSize: 11 }} width={60} />
          <ReferenceLine y={0} stroke="var(--text-muted)" strokeDasharray="4 4" strokeWidth={1} />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const point = payload[0]?.payload as EquityPoint | undefined;
              const n = Number(payload[0]?.value ?? 0);
              return (
                <div
                  style={{
                    background: "var(--bg-card)",
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    padding: "8px 10px",
                  }}
                >
                  <div className="text-xs text-[var(--text-muted)]">{dateTime(point?.date || "")}</div>
                  <div
                    className="mt-0.5 text-sm font-medium tabular-nums"
                    style={{ color: n >= 0 ? profitColor : lossColor }}
                  >
                    PnL: {formatMoney(n)}
                  </div>
                </div>
              );
            }}
          />
          <Line
            type="monotone"
            dataKey="pnl"
            stroke={`url(#${gradId})`}
            strokeWidth={2}
            dot={(props) => {
              const { cx, cy, payload } = props;
              if (cx == null || cy == null || !payload?.isTrade) {
                return <g key={`dot-skip-${payload?.t ?? "x"}`} />;
              }
              const fill = (payload.pnl ?? 0) >= 0 ? profitColor : lossColor;
              return (
                <circle
                  key={`dot-${payload.t}`}
                  cx={cx}
                  cy={cy}
                  r={3}
                  fill={fill}
                  strokeWidth={0}
                />
              );
            }}
            activeDot={{ r: 5 }}
          />
        </LineChart>
      ) : null}
    </div>
  );
}
